import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ReservationAlertProvider, useReservationAlert } from './components/ReservationAlertContext';   // RESERVIERUNGSKETTE-001
import api from './services/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Rides from './pages/Rides';
import Reservations from './pages/Reservations';
import Dispatch from './pages/Dispatch';   // ADMIN-FAHRT-ZUWEISUNG-001
import Users from './pages/Users';
import AccountProtectionOverview from './pages/AccountProtectionOverview';
import Drivers from './pages/Drivers';
import Partners from './pages/Partners';
import Documents from './pages/Documents';
import Ablaufdaten from './pages/Ablaufdaten';   // FAHRER-KI-BACKEND-001 (Teil B)
import Payouts from './pages/Payouts';
import Vehicles from './pages/Vehicles';
import LiveMap from './pages/LiveMap';
import LocationTrust from './pages/LocationTrust';
import DriverRadar from './pages/DriverRadar';
import Finance from './pages/Finance';
import Settlements from './pages/Settlements';
import Settings from './pages/Settings';
import BonusCentral from './pages/BonusCentral';
import MailTemplates from './pages/MailTemplates';
import Invoices from './pages/Invoices';
import Engine from './pages/Engine';
import UltimatePricingV2 from './pages/UltimatePricingV2';
import Pulse from './pages/Pulse';
import AgentPanel from './pages/AgentPanel';
import RouteDispatch from './pages/RouteDispatch';
import LostItems from './pages/LostItems';
import Chat from './pages/Chat';
import Tickets from './pages/Tickets';
import Team from './pages/Team';
import Verifications from './pages/Verifications';
import VerificationReview from './pages/VerificationReview';
import { ReviewInboxProvider, useReviewInbox } from './components/ReviewInboxContext';
import CommandPalette from './components/CommandPalette';
import { bereichLabel, deepLinkForEvent, kindLabel } from './lib/reviewLogic.mjs';
import Ops from './pages/Ops';
import Maintenance from './pages/Maintenance';
import AppGate from './pages/AppGate';
import DataExplorer from './pages/DataExplorer';
import Errors from './pages/Errors';
import OrderLog from './pages/OrderLog';
import Legal from './pages/Legal';
import Assistant from './pages/Assistant';
import Support from './pages/Support';
import Broadcast from './pages/Broadcast';
import { clearBroadcastRecovery } from './pages/broadcastRecovery';
import FaqLuecken from './pages/FaqLuecken';
import ChatVoiceConfig from './pages/ChatVoiceConfig';
import { ADMIN_NAV_GROUPS, ADMIN_NAV_LINKS, filterNavigation, hasPermission, visibleNavLinks } from './navigation';
import 'leaflet/dist/leaflet.css';
import { Bell, Car, ChevronDown, LogOut, Menu, Search, X } from 'lucide-react';
import { datumUndZeit } from './lib/zeit';

const AuthContext = React.createContext();
export const useAuth = () => React.useContext(AuthContext);

// Handy-Erkennung bleibt auch bei Rotation und Größenwechsel synchron.
function useIsMobile() {
  const [mobile, setMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 640);
  useEffect(() => {
    const sync = () => setMobile(window.innerWidth < 640);
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);
  return mobile;
}

// ── DOC-LIFECYCLE-001 (Phase D): Badge-Zahl an Nav-Einträgen (Review-Inbox je Bereich). ──
// Zähler sinkt ERST nach erfolgreicher Entscheidung (Context räumt das Event dann).
function NavBadge({ count, expanded }) {
  if (!count) return null;
  if (!expanded) return <span className="vg-nav-badge-dot" />;
  return (
    <span className="vg-nav-badge">
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Persistente Review-Inbox im Header: Glocke + Gesamtzähler + Event-Liste mit
// kind-Unterscheidung (Erstupload/Reupload/Erneuerung/Selfie) und Deep-Link in die Prüfung.
function InboxBell() {
  const { badges, events, live } = useReviewInbox();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const fmt = (d) => datumUndZeit(d, { jahr: false });
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-label="Review-Inbox" title={live ? 'Live verbunden' : 'Abgleich per Intervall'}
        style={{ position: 'relative', background: 'none', border: 'none', color: '#374151', display: 'flex', padding: 6 }}>
        <Bell size={19} />
        {badges.total > 0 && (
          <span style={{ position: 'absolute', top: 0, right: -2, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: '#9C27B0', color: '#fff', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {badges.total > 99 ? '99+' : badges.total}
          </span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          <div style={{ position: 'absolute', right: 0, top: 34, width: 340, maxWidth: 'calc(100vw - 24px)', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 16px 44px -12px rgba(20,10,40,.35)', zIndex: 91, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#0a0a0a' }}>Offene Prüfungen ({badges.total})</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: live ? '#9C27B0' : '#6b7280', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: live ? '#9C27B0' : '#6b7280' }} />{live ? 'Live' : 'Intervall'}
              </span>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              {events.length === 0 && <div style={{ padding: 16, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>Nichts offen — alles entschieden.</div>}
              {events.slice(0, 30).map(ev => (
                <button key={ev.id} onClick={() => { setOpen(false); navigate(deepLinkForEvent(ev)); }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', textAlign: 'left', padding: '9px 14px', background: 'none', border: 'none', borderBottom: '1px solid #f3f4f6' }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#0a0a0a' }}>{kindLabel(ev.kind)} · {bereichLabel(ev.bereich)}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#4b5563' }}>{ev.kind === 'selfie' ? `Verifizierung #${ev.document_id}` : `Dokument #${ev.document_id}`} · {fmt(ev.created_at)}</span>
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#9C27B0', whiteSpace: 'nowrap' }}>Prüfen →</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AdminLayout({ children }) {
  const { user, logout, can } = useAuth();
  const { badges: reviewBadges } = useReviewInbox();   // Review-Inbox-Badges je Bereich (Phase D)
  const { summary: reservationSummary } = useReservationAlert();   // RESERVIERUNGSKETTE-001
  // RESERVIERUNG-KOMPLETT-001: Zähler = Kachel „Braucht Sie jetzt“ (needs_admin); ohne Wert der bisherige Zähler.
  const badges = { ...reviewBadges, reservierungen: (reservationSummary.needs_admin ?? reservationSummary.attention) || 0 };
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!mobile);   // auf dem Handy startet die Seitenleiste eingeklappt
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const [groupsCollapsed, setGroupsCollapsed] = useState(() => { try { return JSON.parse(localStorage.getItem('adm_nav_groups') || '{}'); } catch { return {}; } });
  const toggleGroup = (id) => setGroupsCollapsed(c => { const next = { ...c, [id]: !c[id] }; try { localStorage.setItem('adm_nav_groups', JSON.stringify(next)); } catch {} return next; });
  const isProd = /voogo\.de/i.test(process.env.REACT_APP_API_URL || '');
  const expanded = mobile ? true : sidebarOpen;          // Off-Canvas-Drawer auf dem Handy: immer mit Beschriftung
  const closeNav = () => { setNavQuery(''); if (mobile) setSidebarOpen(false); };

  const nav = visibleNavLinks(ADMIN_NAV_LINKS, can);
  const visibleNav = filterNavigation(nav, navQuery);
  const current = ADMIN_NAV_LINKS.find(n => n.end ? location.pathname === n.to : (n.to !== '/' && location.pathname.startsWith(n.to)));
  const pageTitle = current ? current.label : 'Admin';

  // ⌘K/Ctrl+K öffnet die globale Command-Palette — von JEDEM Screen, unabhängig davon,
  // ob die Sidebar ein- oder ausgeklappt ist (die Sidebar-Bereichssuche bleibt für die Maus).
  useEffect(() => {
    const onShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLocaleLowerCase('de') === 'k') {
        event.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onShortcut);
    return () => window.removeEventListener('keydown', onShortcut);
  }, []);

  return (
    <div className="vg-portal-shell">
      <aside className={`vg-sidebar ${expanded ? 'is-expanded' : 'is-collapsed'} ${mobile ? 'is-mobile' : ''} ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="vg-sidebar-brand">
          {expanded && (
            <div className="vg-wordmark-wrap">
              <span className="vg-wordmark">V<span>OO</span>GO</span>
              <span className="vg-product-label">Admin Workspace</span>
            </div>
          )}
          <button
            className="vg-icon-button"
            onClick={() => mobile ? setSidebarOpen(false) : setSidebarOpen(o => !o)}
            aria-label={mobile || sidebarOpen ? 'Navigation einklappen' : 'Navigation ausklappen'}
            title={mobile || sidebarOpen ? 'Navigation einklappen' : 'Navigation ausklappen'}
          >
            {(mobile || sidebarOpen) ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
        {expanded && (
          <label className="vg-nav-search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={navQuery}
              onChange={(event) => setNavQuery(event.target.value)}
              placeholder="Bereiche durchsuchen"
              aria-label="Admin-Bereiche durchsuchen"
            />
          </label>
        )}
        <nav className="vg-sidebar-nav" aria-label="Admin-Navigation">
          {ADMIN_NAV_GROUPS.map((g) => {
            const items = visibleNav.filter((n) => n.group === g.id);
            if (!items.length) return null;
            const open = navQuery ? true : !groupsCollapsed[g.id];
            return (
              <section className="vg-nav-section" key={g.id}>
                {expanded ? (
                  <button className="adm-grp" onClick={() => toggleGroup(g.id)} title={open ? 'Gruppe einklappen' : 'Gruppe ausklappen'} aria-expanded={open}>
                    <span className="adm-grp-label">{g.label}</span>
                    <ChevronDown className="adm-chev" size={14} style={{ transform: open ? 'none' : 'rotate(-90deg)' }} />
                  </button>
                ) : (
                  <div className="vg-nav-divider" />
                )}
                {(open || !expanded) && items.map(({ to, icon: Icon, label, end, badgeKey }) => (
                  <NavLink key={to} to={to} end={end} title={!expanded ? label : undefined} onClick={closeNav}
                    className={({ isActive }) => 'adm-item' + (isActive ? ' active' : '')}
                    style={!expanded ? { justifyContent: 'center', position: 'relative' } : { position: 'relative' }}>
                    <Icon size={18} strokeWidth={1.8} />
                    {expanded && <span className="vg-nav-label">{label}</span>}
                    {badgeKey && <NavBadge count={badges[badgeKey]} expanded={expanded} />}
                  </NavLink>
                ))}
              </section>
            );
          })}
          {expanded && visibleNav.length === 0 && (
            <div className="vg-nav-empty">Kein Bereich gefunden.</div>
          )}
        </nav>
        <div className="vg-sidebar-footer">
          <button className="vg-logout-button" onClick={() => { logout(); navigate('/login'); }}>
            <LogOut size={18} />
            {expanded && <span>Abmelden</span>}
          </button>
        </div>
      </aside>

      {mobile && sidebarOpen && <button className="vg-mobile-scrim" onClick={() => setSidebarOpen(false)} aria-label="Navigation schließen" />}

      <div className="vg-shell-content">
        <header className="vg-topbar">
          <div className="vg-topbar-title-wrap">
            {mobile && <button onClick={() => setSidebarOpen(true)} aria-label="Menü öffnen" className="vg-icon-button"><Menu size={22} /></button>}
            <div className="vg-page-heading">
              <span className="vg-page-context">Operations Center · Admin</span>
              <strong>{pageTitle}</strong>
            </div>
          </div>
          <div className="vg-topbar-actions">
            <button type="button" className="vg-cmdk-trigger" onClick={() => setPaletteOpen(true)} title="Suchen (⌘K)" aria-label="Suchen (⌘K)">
              <Search size={15} aria-hidden="true" />
              <span className="vg-cmdk-trigger-label">Suchen</span>
              <kbd>⌘K</kbd>
            </button>
            <InboxBell />
            <span className={`vg-environment ${isProd ? 'is-live' : ''}`}>
              <span className="vg-environment-dot" />
              {isProd ? 'Produktiv' : 'Lokal'}
            </span>
            <div className="vg-user-summary">
              <div className="vg-avatar">
                {user?.name?.charAt(0)}
              </div>
              <span>
                <strong>{user?.name}</strong>
                <small>Administrator</small>
              </span>
            </div>
          </div>
        </header>
        <main className="vg-shell-main"><div key={location.pathname} className="vg-page">{children}</div></main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} can={can} />
    </div>
  );
}

function AppRoutes() {
  const { user, loading, can, sessionError, restoreSession } = useAuth();

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f5f5f5' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ marginBottom: 12 }}><Car size={48} /></div>
        <div style={{ color: '#4b5563' }}>Admin wird geladen...</div>
      </div>
    </div>
  );

  if (sessionError) return (
    <main style={{ maxWidth: 480, margin: '15vh auto', padding: 24 }}>
      <h1>Anmeldung wird geprüft</h1>
      <p role="alert">Die Verbindung ist gerade gestört. Deine Anmeldung wurde beibehalten. Bitte versuche es erneut.</p>
      <button type="button" onClick={restoreSession}>Erneut versuchen</button>
    </main>
  );

  // Eingeloggte Seiten: Review-Inbox-Provider (Badges/Socket/Polling) UM das Layout,
  // damit Sidebar-Badges, Header-Glocke und die Seiten denselben Zähler sehen.
  const wrap = (el) => (user ? <ReviewInboxProvider><ReservationAlertProvider><AdminLayout>{el}</AdminLayout></ReservationAlertProvider></ReviewInboxProvider> : <Navigate to="/login" />);
  const wrapPerm = (el, permissions) => !user
    ? <Navigate to="/login" />
    : permissions.some((permission) => can && can(permission))
      ? <ReviewInboxProvider><ReservationAlertProvider><AdminLayout>{el}</AdminLayout></ReservationAlertProvider></ReviewInboxProvider>
      : <Navigate to="/" replace />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={wrap(<Dashboard />)} />
      <Route path="/ops" element={wrap(<Ops />)} />
      <Route path="/rides" element={wrap(<Rides />)} />
      <Route path="/reservierungen" element={wrap(<Reservations />)} />
      <Route path="/dispatch" element={wrap(<Dispatch />)} />
      <Route path="/order-log" element={wrap(<OrderLog />)} />
      <Route path="/users" element={wrap(<Users />)} />
      <Route path="/account-protection" element={wrapPerm(<AccountProtectionOverview />, ['account_protection.view', 'account_protection.manage'])} />
      <Route path="/drivers" element={wrap(<Drivers />)} />
      <Route path="/partners" element={wrap(<Partners />)} />
      <Route path="/documents" element={wrap(<Documents />)} />
      <Route path="/documents/ablaufdaten" element={wrapPerm(<Ablaufdaten />, ['freigaben.manage', 'freigaben.view'])} />
      <Route path="/verifications" element={wrap(<Verifications />)} />
      {/* Phase E: Selfie-Vergleichsseite — alle 6 Flächen gleichzeitig, eine Seite je Verifizierung. */}
      <Route path="/verifications/:id" element={wrap(<VerificationReview />)} />
      <Route path="/vehicles-admin" element={wrap(<Vehicles />)} />
      <Route path="/payouts" element={wrap(<Payouts />)} />
      <Route path="/livemap" element={wrap(<LiveMap />)} />
      <Route path="/location-trust" element={wrapPerm(<LocationTrust />, ['location_trust.view', 'location_trust.manage', 'location_trust.appeal', 'location_trust.config'])} />
      <Route path="/driver-radar" element={wrapPerm(<DriverRadar />, ['driver_radar.view', 'driver_radar.manage'])} />
      <Route path="/finance" element={wrap(<Finance />)} />
      <Route path="/settlements" element={wrapPerm(<Settlements />, ['finance.manage'])} />
      <Route path="/settings" element={wrap(<Settings />)} />
      <Route path="/bonus" element={wrapPerm(<BonusCentral />, ['settings.manage', 'finance.manage'])} />
      {/* MAIL-VORLAGEN-001: E-Mail-Texte ohne Deploy ändern. Der Server verlangt settings.manage. */}
      <Route path="/mail-templates" element={wrapPerm(<MailTemplates />, ['settings.manage'])} />
      <Route path="/invoices" element={wrap(<Invoices />)} />
      <Route path="/legal" element={wrap(<Legal />)} />
      <Route path="/system" element={wrap(<Maintenance />)} />
      <Route path="/app-gate" element={wrap(<AppGate />)} />
      {/* ADMIN-CHATVOICE-UI-001: Owner-Bereich ('*'). Der Server verweigert jedem anderen Admin
          den Zugriff (403, nicht delegierbar) — die Oberflaeche fuehrt gar nicht erst hin. */}
      <Route path="/chat-voice" element={wrapPerm(<ChatVoiceConfig />, ['*'])} />
      <Route path="/errors" element={wrap(<Errors />)} />
      <Route path="/data" element={wrap(<DataExplorer />)} />
      <Route path="/engine" element={wrap(<Engine />)} />
      {/* ULT-PRICING-V2: Admin-Dashboard (ruhend hinter Modus/Kill-Switch) — Route erhalten (REL-1.223) */}
      <Route path="/pulse" element={wrapPerm(<Pulse accountId={user?.id} />, ['settings.manage', 'engine.manage', 'engine.view'])} />
      <Route path="/ultimate-v2" element={wrap(<UltimatePricingV2 />)} />
      <Route path="/agent" element={wrapPerm(<AgentPanel />, ['agent.view', 'agent.manage', 'settings.manage'])} />
      <Route path="/route-dispatch" element={wrapPerm(<RouteDispatch />, ['settings.manage'])} />
      <Route path="/pricing" element={<Navigate to="/ultimate-v2" replace />} />
      <Route path="/lost-items" element={wrap(<LostItems />)} />
      <Route path="/support" element={wrap(<Support />)} />
      <Route path="/support/nachrichten" element={wrap(<Broadcast accountId={user?.id} />)} />
      <Route path="/support/faq-luecken" element={wrap(<FaqLuecken />)} />
      <Route path="/tickets" element={wrap(<Tickets />)} />
      <Route path="/chat" element={wrap(<Chat />)} />
      <Route path="/assistant" element={wrap(<Assistant />)} />
      <Route path="/team" element={wrap(<Team />)} />
    </Routes>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [sessionError, setSessionError] = useState(false);
  const sessionRequest = useRef(0);

  const restoreSession = useCallback(async () => {
    const request = ++sessionRequest.current;
    const token = localStorage.getItem('admin_token');
    setUser(null);
    setSessionError(false);
    setLoading(true);
    if (!token) {
      clearBroadcastRecovery();
      setLoading(false);
      return;
    }
    const current = () => request === sessionRequest.current
      && localStorage.getItem('admin_token') === token;
    try {
      const response = await api.get('/auth/me', { timeout: 10000 });
      if (!current()) return;
      const confirmedUser = response.data?.user;
      if (!confirmedUser?.role) throw new Error('session_response_invalid');
      if (confirmedUser.role === 'admin') setUser(confirmedUser);
      else localStorage.removeItem('admin_token');
    } catch (error) {
      // The API interceptor may already have removed an actually rejected token.
      if (request !== sessionRequest.current) return;
      const stored = localStorage.getItem('admin_token');
      if (stored !== token && stored !== null) return;
      if (error.response?.status === 401) localStorage.removeItem('admin_token');
      else if (stored === token) setSessionError(true);
    } finally {
      if (request === sessionRequest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    restoreSession();
    return () => { sessionRequest.current += 1; };
  }, [restoreSession]);

  const login = async (email, password, totp, turnstile_token, stillActive = () => true) => {
    const request = ++sessionRequest.current;
    const token = localStorage.getItem('admin_token');
    const current = () => request === sessionRequest.current
      && localStorage.getItem('admin_token') === token && stillActive();
    try {
      const res = await api.post('/auth/login', { email, password, totp, role: 'admin', turnstile_token }, { timeout: 15000 });
      if (!current()) return false;
      if (res.data.user.role !== 'admin') throw new Error('Kein Admin-Konto');
      localStorage.setItem('admin_token', res.data.token);
      await restoreSession(); // Rechte erst aus der bestaetigten Sitzung uebernehmen.
      return request + 1 === sessionRequest.current && stillActive()
        && localStorage.getItem('admin_token') === res.data.token;
    } catch (error) {
      if (!current()) return false;
      throw error;
    }
  };

  // Rechte-Helfer: '*' = Vollzugriff, sonst die konkrete Berechtigung.
  // Die Semantik liegt in navigation.js, damit Navigation, Routen-Guard und Tests EINE Quelle haben.
  const can = (perm) => hasPermission(user?.permissions, perm);

  const logout = () => {
    sessionRequest.current += 1;
    setSessionError(false);
    setLoading(false);
    api.post('/auth/logout').catch(() => {}); // Datei-Zugriffs-Cookie serverseitig löschen
    clearBroadcastRecovery();
    localStorage.removeItem('admin_token'); setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, sessionError, restoreSession }}>
      <BrowserRouter basename="/">
        <Toaster position="top-right" toastOptions={{ style: { background: '#e5e7eb', color: '#0a0a0a', borderRadius: 10 } }} />
        <AppRoutes />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
